import "./styles.css";

import {
  Avatar,
  Checkbox,
  ConfigProvider,
  Empty,
  Input,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  MinusCircleOutlined,
} from "@ant-design/icons";
import type {
  ComponentRow,
  HistoryRecord,
  ReportStatus,
  WorkGroup,
  WorkItem,
  WorkTask,
} from "./data";
import { useEffect, useState } from "react";

import type { ColumnsType } from "antd/es/table";
import HistoryModal from "./HistoryModal";
import dayjs from "dayjs";
import { orpcClient } from "@/lib/orpc-client";
import { single } from "@/lib/orpc-batch";
import { useProjectIfc3d, assemblyMarks } from "../useProjectIfc3d";

const { Link } = Typography;

const NODE_TYPE_LABEL: Record<string, string> = {
  building: "建築本體",
  zone: "分區",
  batch: "分批",
  package: "構件包",
};

const BOM_COLUMN_LABELS: { key: string; label: string }[] = [
  { key: "qdId", label: "廣達ID" },
  { key: "componentNo", label: "構件編號" },
  { key: "weight", label: "總重量(單支)" },
  { key: "zone", label: "區別" },
  { key: "shippingNo", label: "拆運號碼" },
  { key: "drawing", label: "圖面名稱" },
  { key: "section", label: "斷面規格" },
];

const WORK_COLUMN_LABELS: { key: string; label: string }[] = [
  { key: "worker", label: "施工人員" },
  { key: "status", label: "狀態" },
  { key: "reportDate", label: "回報日期" },
];

export default function ProductionResumeTable({
  projectId,
}: {
  projectId?: string;
}) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<ComponentRow[]>([]);
  const [groups, setGroups] = useState<WorkGroup[]>([]);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState({
    qdId: "",
    componentNo: "",
    zone: "",
    shippingNo: "",
    section: "",
  });

  const ifc3d = useProjectIfc3d(projectId);

  const COLUMN_VISIBILITY_STORAGE_KEY = `productionResume_columnVisibility_${projectId ?? "all"}`;

  const [hiddenColumns, setHiddenColumns] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(
        `productionResume_columnVisibility_${projectId ?? "all"}`,
      );
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      const saved = localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
      setHiddenColumns(saved ? JSON.parse(saved) : []);
    } catch {
      setHiddenColumns([]);
    }
  }, [COLUMN_VISIBILITY_STORAGE_KEY]);

  const toggleColumn = (key: string, checked: boolean) => {
    const next = checked
      ? hiddenColumns.filter((k) => k !== key)
      : [...hiddenColumns, key];
    setHiddenColumns(next);
    localStorage.setItem(COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify(next));
  };

  const updateFilter = (key: keyof typeof filters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const filteredRows = rows.filter((row) => {
    const match = (value: string, keyword: string) =>
      String(value || "")
        .toLowerCase()
        .includes((keyword || "").toLowerCase());

    return (
      match(row.qdId, filters.qdId) &&
      match(row.componentNo, filters.componentNo) &&
      match(row.zone, filters.zone) &&
      match(row.shippingNo, filters.shippingNo) &&
      match(row.section, filters.section)
    );
  });

  const findMatchingPdf = (drawingName: string) => {
    if (!drawingName || !attachments?.length) return null;
    return attachments.find((file: any) => {
      if (!file?.name || !file?.url) return false;
      const isPdf = file.name.toLowerCase().endsWith(".pdf");
      if (!isPdf) return false;
      const fileNameWithoutExt = file.name.replace(/\.pdf$/i, "");
      return fileNameWithoutExt.trim() === drawingName.trim();
    });
  };

  const [open, setOpen] = useState(false);
  const [activeRow, setActiveRow] = useState<ComponentRow | null>(null);
  const [activeTask, setActiveTask] = useState<WorkTask | null>(null);
  const [activeTitle, setActiveTitle] = useState("");

  useEffect(() => {
    if (!projectId) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setPage(1);
      setFilters({
        qdId: "",
        componentNo: "",
        zone: "",
        shippingNo: "",
        section: "",
      });
      try {
        const project = await single(
          orpcClient.project.read([{ id: projectId }]),
        );
        const nodes = (project?.projectNode ?? []) as any[];
        if (!cancelled) setAttachments((project?.attachment ?? []) as any[]);
        const buildingNode = nodes.find((n) => n.nodeType === "building");

        if (!buildingNode) {
          if (!cancelled) {
            setRows([]);
            setGroups([]);
          }
          return;
        }

        const assemblies = (await orpcClient.steelAssembly.list({
          filter: { buildingId: buildingNode.id },
        })) as any[];

        const nodeWorkItems = await Promise.all(
          nodes.map(async (node) => {
            const workItems = (await orpcClient.workItem.list({
              filter: { projectNodeId: node.id },
            })) as any[];
            return { node, workItems };
          }),
        );

        const activeNodeWorkItems = nodeWorkItems
          .filter((n) => n.workItems.length > 0)
          .map((n) => ({
            node: n.node,
            workItems: [...n.workItems].sort(
              (a, b) => (a.order ?? 0) - (b.order ?? 0),
            ),
          }));

        const allWorkItems = activeNodeWorkItems.flatMap((n) => n.workItems);

        const scanHistoryByWorkItem = await Promise.all(
          allWorkItems.map(async (wi) => {
            const history = (await orpcClient.scanHistory.list({
              filter: { workItemId: wi.id },
            })) as any[];
            return { workItemId: wi.id, history };
          }),
        );

        // tasks[steelAssemblyId][workItemId] = HistoryRecord[]
        const recordsMap: Record<string, Record<string, HistoryRecord[]>> = {};

        scanHistoryByWorkItem.forEach(({ workItemId, history }) => {
          history.forEach((rec) => {
            const record: HistoryRecord = {
              id: rec.id,
              status: rec.scanStatus as ReportStatus,
              description: rec.description || "",
              personName: rec.employee?.name || "N/A",
              personPhoto: rec.employee?.profile || undefined,
              photo: rec.photoUrl || undefined,
              reportedAt: rec.reportTime
                ? dayjs(rec.reportTime).format("YYYY-MM-DD HH:mm")
                : "",
              revoked: Boolean(rec.revokedAt),
            };

            (rec.steelAssembly ?? []).forEach((link: any) => {
              const saId = link.steelAssemblyId;
              if (!saId) return;
              if (!recordsMap[saId]) recordsMap[saId] = {};
              if (!recordsMap[saId][workItemId])
                recordsMap[saId][workItemId] = [];
              recordsMap[saId][workItemId].push(record);
            });
          });
        });

        const buildTask = (saId: string, workItemId: string): WorkTask => {
          const history = (recordsMap[saId]?.[workItemId] ?? [])
            .slice()
            .sort(
              (a, b) =>
                dayjs(b.reportedAt).valueOf() - dayjs(a.reportedAt).valueOf(),
            );
          const latestValid = history.find((h) => !h.revoked);
          return {
            worker: latestValid?.personName ?? "",
            workerPhoto: latestValid?.personPhoto,
            status: latestValid ? latestValid.status : null,
            reportDate: latestValid?.reportedAt ?? "",
            history,
          };
        };

        const componentRows: ComponentRow[] = assemblies.map((sa) => {
          const tasks: Record<string, WorkTask> = {};
          allWorkItems.forEach((wi) => {
            tasks[wi.id] = buildTask(sa.id, wi.id);
          });
          return {
            key: sa.id,
            qdId: sa.guandaId
              ? String(sa.guandaId).padStart(7, "0")
              : sa.steelAssemblyId || "-",
            componentNo: sa.steelAssemblyCode || "-",
            weight: sa.totalWeight != null ? `${sa.totalWeight} kg` : "-",
            zone: sa.distinction || "-",
            shippingNo: sa.transportNumber || "-",
            drawing: sa.drawingName || "",
            section: sa.metricSizeMark || "-",
            steelAssemblyCode: sa.steelAssemblyCode ?? undefined,
            steelAssemblyId: sa.steelAssemblyId ?? undefined,
            tasks,
          };
        });

        const workGroups: WorkGroup[] = activeNodeWorkItems.map(
          ({ node, workItems }) => ({
            group: node.name || NODE_TYPE_LABEL[node.nodeType] || node.nodeType,
            items: workItems.map(
              (wi): WorkItem => ({ key: wi.id, label: wi.name }),
            ),
          }),
        );

        if (!cancelled) {
          setRows(componentRows);
          setGroups(workGroups);
        }
      } catch (error) {
        console.error("Error loading production resume:", error);
        if (!cancelled) {
          setRows([]);
          setGroups([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const openHistory = (row: ComponentRow, item: WorkItem) => {
    setActiveRow(row);
    setActiveTask(row.tasks[item.key]);
    setActiveTitle(item.label);
    setOpen(true);
  };

  const statusTag = (status: ReportStatus | null) => {
    if (status === "success")
      return (
        <Tag icon={<CheckCircleFilled />} color="success">
          完成
        </Tag>
      );
    if (status === "error")
      return (
        <Tag icon={<CloseCircleFilled />} color="error">
          錯誤
        </Tag>
      );
    return (
      <Tag icon={<MinusCircleOutlined />} color="default">
        未回報
      </Tag>
    );
  };

  const buildWorkChildren = (item: WorkItem) => [
    {
      title: "施工人員",
      columnKey: "worker",
      key: `${item.key}-worker`,
      width: 150,
      render: (_: unknown, row: ComponentRow) => {
        const task = row.tasks[item.key];
        if (!task?.worker) return <span style={{ color: "#bfbfbf" }}>—</span>;
        return (
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            <Avatar size={32} src={task.workerPhoto}>
              {task.worker.slice(0, 1)}
            </Avatar>
            {task.worker}
          </span>
        );
      },
    },
    {
      title: "狀態",
      columnKey: "status",
      key: `${item.key}-status`,
      width: 100,
      align: "center" as const,
      render: (_: unknown, row: ComponentRow) =>
        statusTag(row.tasks[item.key]?.status ?? null),
    },
    {
      title: "回報日期",
      columnKey: "reportDate",
      key: `${item.key}-date`,
      width: 160,
      render: (_: unknown, row: ComponentRow) => {
        const task = row.tasks[item.key];
        if (!task || task.history.length === 0)
          return <span style={{ color: "#bfbfbf" }}>—</span>;
        return (
          <Link
            href="#"
            onClick={(e) => {
              e.preventDefault();
              openHistory(row, item);
            }}
          >
            {task.reportDate || "查看紀錄"}
          </Link>
        );
      },
    },
  ]
    .filter((col) => !hiddenColumns.includes(col.columnKey))
    .map(({ columnKey, ...col }) => col);

  const bomChildren = [
    {
      title: "廣達ID",
      dataIndex: "qdId",
      key: "qdId",
      width: 100,
    },
    {
      title: "構件編號",
      dataIndex: "componentNo",
      key: "componentNo",
      width: 110,
    },
    {
      title: "總重量(單支)",
      dataIndex: "weight",
      key: "weight",
      width: 120,
    },
    { title: "區別", dataIndex: "zone", key: "zone", width: 80 },
    {
      title: "拆運號碼",
      dataIndex: "shippingNo",
      key: "shippingNo",
      width: 110,
    },
    {
      title: "圖面名稱",
      dataIndex: "drawing",
      key: "drawing",
      width: 150,
      render: (drawing: string) => {
        if (!drawing) return <span style={{ color: "#bfbfbf" }}>—</span>;
        const matchingPdf = findMatchingPdf(drawing);
        if (matchingPdf) {
          return (
            <a
              href={matchingPdf.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1890ff", textDecoration: "underline" }}
            >
              {drawing}
            </a>
          );
        }
        return drawing;
      },
    },
    ...(ifc3d.enabled
      ? [
          {
            title: ifc3d.title,
            dataIndex: "model3d",
            key: "model3d",
            width: 100,
            render: (_: unknown, row: ComponentRow) =>
              ifc3d.render3dCell(assemblyMarks(row)),
          },
        ]
      : []),
    {
      title: "斷面規格",
      dataIndex: "section",
      key: "section",
      width: 150,
    },
  ]
    .filter((col) => !hiddenColumns.includes(col.key))
    .map((col) => ({ ...col, fixed: "left" as const }));

  const columns: ColumnsType<ComponentRow> = [
    ...(bomChildren.length
      ? [
          {
            title: "生產計劃BOM",
            className: "group-bom",
            fixed: "left" as const,
            children: bomChildren,
          },
        ]
      : []),
    ...groups
      .map((group) => ({
        title: group.group,
        className: "group-zone",
        children: group.items
          .map((item) => ({
            title: item.label,
            className: "group-work",
            children: buildWorkChildren(item),
          }))
          .filter((item) => item.children.length > 0),
      }))
      .filter((group) => group.children.length > 0),
  ];

  return (
    <ConfigProvider
      theme={{
        components: {
          Table: {
            headerBg: "#fafafa",
            headerColor: "#1f1f1f",
            borderColor: "#f0f0f0",
          },
        },
      }}
    >
      <div className="mb-4 grid grid-cols-5 gap-4">
        <div>
          <div className="mb-2 text-sm font-medium text-gray-700">廣達ID</div>
          <Input
            placeholder="搜尋廣達ID"
            value={filters.qdId}
            onChange={(e) => updateFilter("qdId", e.target.value)}
            allowClear
          />
        </div>
        <div>
          <div className="mb-2 text-sm font-medium text-gray-700">構件編號</div>
          <Input
            placeholder="搜尋構件編號"
            value={filters.componentNo}
            onChange={(e) => updateFilter("componentNo", e.target.value)}
            allowClear
          />
        </div>
        <div>
          <div className="mb-2 text-sm font-medium text-gray-700">區別</div>
          <Input
            placeholder="搜尋區別"
            value={filters.zone}
            onChange={(e) => updateFilter("zone", e.target.value)}
            allowClear
          />
        </div>
        <div>
          <div className="mb-2 text-sm font-medium text-gray-700">拆運號碼</div>
          <Input
            placeholder="搜尋拆運號碼"
            value={filters.shippingNo}
            onChange={(e) => updateFilter("shippingNo", e.target.value)}
            allowClear
          />
        </div>
        <div>
          <div className="mb-2 text-sm font-medium text-gray-700">斷面規格</div>
          <Input
            placeholder="搜尋斷面規格"
            value={filters.section}
            onChange={(e) => updateFilter("section", e.target.value)}
            allowClear
          />
        </div>
      </div>

      <div
        className="mb-4 flex items-start gap-4 px-4 py-3"
        style={{ backgroundColor: "#F5F5F5", borderRadius: "8px" }}
      >
        <span style={{ fontWeight: 500, minWidth: "80px" }}>顯示欄位:</span>
        <div className="flex flex-wrap gap-3">
          {[
            ...BOM_COLUMN_LABELS.flatMap((col) =>
              col.key === "drawing" && ifc3d.enabled
                ? [col, { key: "model3d", label: ifc3d.title }]
                : [col],
            ),
            ...WORK_COLUMN_LABELS,
          ].map((col) => (
            <Checkbox
              key={col.key}
              checked={!hiddenColumns.includes(col.key)}
              onChange={(e) => toggleColumn(col.key, e.target.checked)}
            >
              {col.label}
            </Checkbox>
          ))}
        </div>
      </div>

      <Table<ComponentRow>
        columns={columns}
        dataSource={filteredRows}
        loading={loading}
        bordered
        pagination={{
          current: page,
          pageSize,
          total: filteredRows.length,
          onChange: (nextPage, nextSize) => {
            setPage(nextPage);
            setPageSize(nextSize);
          },
          showSizeChanger: true,
          pageSizeOptions: ["20", "50", "100", "200"],
          showTotal: (total, range) =>
            `第 ${range[0]}-${range[1]} 筆，共 ${total} 筆`,
        }}
        scroll={{ x: "max-content" }}
        size="middle"
        locale={{ emptyText: <Empty description="尚無生產履歷資料" /> }}
      />
      <HistoryModal
        open={open}
        row={activeRow}
        task={activeTask}
        workLabel={activeTitle}
        onClose={() => setOpen(false)}
      />
      {ifc3d.ifcPreview}
    </ConfigProvider>
  );
}
